import type { Metadata } from "next"
import { publicCountries, getBcp47Locale, type Country } from "@/i18n/config"
import { SITE_URL } from "@/lib/site-url"
import { isMembershipFirstSite } from "@/lib/membership-first"
import { localizedPath } from "@/lib/localized-route-slugs"
import {
  COUNTRY_GATES,
  isAllowedInCountry,
  type GateId,
} from "@/lib/country-gates"

/**
 * Page metadata per locale. Each page has a title and description.
 * Titles follow the pattern: "{Page title}"
 */
const META: Record<
  string,
  Record<string, { title: string; description: string }>
> = {
  home: {
    en: {
      title: "Preventive MRI & blood test check-ups in Switzerland",
      description:
        "Early detection of cancer, liver disease and over 500 other diseases. Receive a personal health action plan with full body MRI and blood tests.",
    },
    de: {
      title: "Ganzkörper-MRI & Bluttests in der Schweiz",
      description:
        "Früherkennung von Krebs, Lebererkrankungen und über 500 weiteren Erkrankungen. Persönlicher Aktionsplan mit Ganzkörper-MRI und Bluttests.",
    },
    // Germany sells the blood check-up and its add-ons, nothing else, and
    // /de/de no longer shows MRI anywhere. The title is the snippet Google
    // shows, so it must not promise the scan the page doesn't offer, and the
    // description drops the "over 500 diseases" claim that rested on the
    // imaging. Restore the shared `de` entry when DE_EXPANSION opens MRI
    // booking in Germany.
    "de-de": {
      title: "Bluttest-Check-ups in Deutschland",
      description:
        "Vorsorge-Starter-Bluttest mit %panelCount% Biomarkern: Früherkennung von Risiken für Herz, Leber, Nieren und Stoffwechsel. Ärztlich geprüfte Ergebnisse mit persönlichem Aktionsplan.",
    },
    // Austria sells the full-body MRI packages only and has no Swiss clinics,
    // so the shared `de` title ("… in der Schweiz") and its blood-test
    // promise don't apply. Override keys skip the market suffix, so the
    // country goes in the title itself.
    "de-at": {
      title: "Ganzkörper-MRT in Österreich",
      description:
        "Früherkennung von Krebs, Lebererkrankungen und weiteren Erkrankungen mit einem Ganzkörper-MRT. Ärztlich geprüfte Ergebnisse mit persönlichem Aktionsplan.",
    },
    fr: {
      title: "IRM préventive & bilans sanguins en Suisse",
      description:
        "Détection précoce du cancer, des maladies du foie et de plus de 500 autres maladies. Recevez un plan d'action santé personnalisé avec IRM corps entier et analyses sanguines.",
    },
    // NL copy rules (NL-market.md): blood tests only for now, Holistic MRI is
    // "coming soon", no screening / early-detection wording (WBO).
    nl: {
      title: "Preventieve bloedtest-check-ups in Nederland",
      description:
        "Inzicht in je gezondheid met de Advanced bloedtest: %panelCount% biomarkers, beoordeeld door Nederlandse artsen, met een persoonlijk actieplan. Holistic MRI binnenkort beschikbaar.",
    },
    "en-nl": {
      title: "Preventive blood test check-ups in the Netherlands",
      description:
        "Insight into your health with the Advanced blood test: %panelCount% biomarkers, reviewed by Dutch doctors, with a personal action plan. Holistic MRI coming soon.",
    },
    "en-de": {
      title: "Blood test check-ups in Germany",
      description:
        "Prevention Starter Blood Test with %panelCount% biomarkers: early insight into risks for your heart, liver, kidneys and metabolism. Doctor-reviewed results with a personal action plan.",
    },
  },
  quizFocus: {
    en: {
      title: "Find your check-up",
      description:
        "Answer a few quick questions and get a personalised preventive check-up plan — full-body MRI and blood tests matched to your health goals.",
    },
    de: {
      title: "Finden Sie Ihren Check-up",
      description:
        "Beantworten Sie ein paar kurze Fragen und erhalten Sie einen persönlichen Vorsorge-Check-up — Ganzkörper-MRI und Bluttests, abgestimmt auf Ihre Gesundheitsziele.",
    },
    fr: {
      title: "Trouvez votre bilan de santé",
      description:
        "Répondez à quelques questions et recevez un bilan de santé préventif personnalisé — IRM corps entier et analyses sanguines adaptés à vos objectifs.",
    },
    // No em-dash: it's the most recognizable AI tell in Dutch (nl-NL language
    // layer), so this uses a colon where en/de/fr use one. Blood only: the NL
    // quiz asks no MRI question (NO_MRI_QUIZ_COUNTRIES in lib/quiz/market.ts),
    // so the snippet must not promise one.
    nl: {
      title: "Vind jouw check-up",
      description:
        "Beantwoord een paar korte vragen en krijg een preventief check-upplan dat bij je past: het bloedonderzoek dat aansluit op je gezondheidsdoelen.",
    },
    "en-nl": {
      title: "Find your check-up",
      description:
        "Answer a few quick questions and get a personalised preventive check-up plan: the blood tests that match your health goals.",
    },
    // Germany sells the Vorsorge-Starter and its blood add-ons only, so the
    // quiz there never asks about imaging and the description must not promise
    // an MRT. Restore the shared `de` entry when DE_EXPANSION opens MRI booking.
    "de-de": {
      title: "Finden Sie Ihren Check-up",
      description:
        "Beantworten Sie ein paar kurze Fragen und erhalten Sie Ihren persönlichen Vorsorge-Check-up: der Vorsorge-Starter und passende Zusatztests, abgestimmt auf Ihre Gesundheitsziele.",
    },
    "en-de": {
      title: "Find your check-up",
      description:
        "Answer a few quick questions and get your personal preventive check-up: the Prevention Starter and matching add-on tests, tailored to your health goals.",
    },
  },
  pricing: {
    en: {
      title: "Pricing for full-body MRI & blood test check-ups",
      description:
        "Transparent pricing for preventive health check-ups in Switzerland: full-body MRI, advanced blood tests and combined packages, reviewed by Swiss doctors.",
    },
    de: {
      title: "Preise für Ganzkörper-MRI & Bluttest-Check-ups",
      description:
        "Transparente Preise für Gesundheits-Check-ups in der Schweiz: Ganzkörper-MRI, erweiterte Bluttests und kombinierte Pakete, von Schweizer Ärzten geprüft.",
    },
    // Germany sells the blood check-up and its add-ons, nothing else, and
    // /de/de/pricing now shows only those. The title is the snippet Google
    // shows, so it must not promise the scan the page no longer prices, and
    // the description makes no Swiss-physician claim — Germany states who
    // reviews results without a nationality, same rule the Austrian copy
    // follows. Restore the shared `de` entry when DE_EXPANSION opens MRI
    // booking.
    "de-de": {
      title: "Preise für Bluttest-Check-ups",
      description:
        "Transparente Preise für den Vorsorge-Starter-Bluttest mit %panelCount% Biomarkern und die Blut-Zusatzanalysen. Ärztlich geprüfte Ergebnisse mit persönlichem Aktionsplan.",
    },
    // Austria sells the full-body MRI packages only, so the pricing snippet
    // must not promise blood-test check-ups. Override keys skip the market
    // suffix, so the country goes in the title itself.
    "de-at": {
      title: "Preise für Ganzkörper-MRT-Check-ups in Österreich",
      description:
        "Transparente Preise für unsere Ganzkörper-MRT-Pakete in Österreich. Ärztlich geprüfte Ergebnisse mit persönlichem Aktionsplan und klaren Empfehlungen.",
    },
    fr: {
      title: "Tarifs pour IRM corps entier & bilans sanguins",
      description:
        "Tarifs transparents pour les bilans de santé préventifs. IRM corps entier, analyses sanguines avancées et forfaits complets.",
    },
    // NL copy rules (NL-market.md): blood tests only for now, Holistic MRI is
    // "coming soon", no screening / early-detection wording (WBO).
    nl: {
      title: "Prijzen voor bloedtest-check-ups",
      description:
        "Transparante prijzen voor de Advanced bloedtest met %panelCount% biomarkers en aanvullende bloedtests. Beoordeeld door Nederlandse artsen, met een persoonlijk actieplan.",
    },
    "en-nl": {
      title: "Pricing for blood test check-ups",
      description:
        "Transparent pricing for the Advanced blood test with %panelCount% biomarkers and add-on blood tests. Reviewed by Dutch doctors, with a personal action plan.",
    },
    "en-de": {
      title: "Pricing for blood test check-ups",
      description:
        "Transparent pricing for the Prevention Starter Blood Test with %panelCount% biomarkers and the blood add-on analyses. Doctor-reviewed results with a personal action plan.",
    },
  },
  reviews: {
    en: {
      title: "Ahead Health reviews — real stories from our members",
      description:
        "Read verified Google and Trustpilot reviews from people who took an Ahead Health full-body MRI or blood test check-up in Switzerland. Quotes, ratings, and what they found.",
    },
    de: {
      title: "Ahead Health Bewertungen — echte Geschichten unserer Mitglieder",
      description:
        "Lesen Sie geprüfte Google- und Trustpilot-Bewertungen von Personen, die in der Schweiz ein Ganzkörper-MRI oder einen Bluttest bei Ahead Health gemacht haben.",
    },
    // Austria: the shared snippet names Switzerland and the blood test.
    "de-at": {
      title: "Bewertungen: echte Erfahrungen unserer Mitglieder",
      description:
        "Lesen Sie geprüfte Google- und Trustpilot-Bewertungen von Menschen, die ihren Check-up bei Ahead gemacht haben: ihre Erfahrungen, Bewertungen und Zitate.",
    },
    fr: {
      title: "Avis Ahead Health — vraies histoires de nos membres",
      description:
        "Lisez les avis vérifiés Google et Trustpilot de personnes ayant passé une IRM corps entier ou un bilan sanguin Ahead Health en Suisse.",
    },
    nl: {
      title: "Ahead Health beoordelingen — echte verhalen van onze leden",
      description:
        "Lees geverifieerde Google- en Trustpilot-reviews van mensen die een check-up bij Ahead Health hebben gedaan.",
    },
    "en-nl": {
      title: "Ahead Health reviews — real stories from our members",
      description:
        "Read verified Google and Trustpilot reviews from people who took an Ahead Health check-up. Quotes, ratings, and what they found.",
    },
    "en-de": {
      title: "Ahead Health reviews: real stories from our members",
      description:
        "Read verified Google and Trustpilot reviews from people who took an Ahead Health check-up. Quotes, ratings, and what they found.",
    },
  },
  howItWorks: {
    en: {
      title: "How our preventive check-ups work",
      description:
        "From booking to results: book online, visit a partner clinic for your MRI or blood test, then get a doctor-reviewed report with a personal action plan.",
    },
    de: {
      title: "So funktionieren unsere Vorsorge-Check-ups",
      description:
        "Von der Buchung bis zum Ergebnis: online buchen, MRI oder Bluttest in einer Partnerklinik und ein ärztlich geprüfter Bericht mit persönlichem Aktionsplan.",
    },
    // Germany sells the blood check-up and nothing else, and /de/de/how-it-works
    // now explains only that: no MRI tab, no package cards. Same reasoning as
    // the `de-de` pricing override above, so the snippet Google shows matches
    // the page. Restore the shared `de` entry when DE_EXPANSION opens MRI
    // booking.
    "de-de": {
      title: "So funktioniert der Ahead Bluttest",
      description:
        "Von der Buchung bis zum Befund: %panelCount% Biomarker aus einer Blutabnahme in einer deutschen Partnerklinik, ärztlich ausgewertet, mit persönlichem Aktionsplan.",
    },
    fr: {
      title: "Comment fonctionnent nos bilans préventifs",
      description:
        "De la réservation aux résultats : réservez en ligne, faites votre IRM ou bilan sanguin dans un centre partenaire et recevez un rapport validé par un médecin.",
    },
    nl: {
      title: "Hoe onze preventieve check-ups werken",
      description:
        "Van boeking tot resultaten — ontdek stap voor stap hoe onze preventieve gezondheidsonderzoeken werken.",
    },
    "en-de": {
      title: "How the Ahead blood test works",
      description:
        "From booking to results: %panelCount% biomarkers from one blood draw at a German partner clinic, reviewed by a doctor, with a personal action plan.",
    },
    "de-at": {
      title: "So funktionieren unsere Vorsorge-Check-ups – Österreich",
      description:
        "Von der Anmeldung bis zum Befund: So läuft Ihr Ganzkörper-MRT-Check-up bei Ahead ab, mit ärztlich geprüften Ergebnissen und einem persönlichen Aktionsplan.",
    },
    "en-nl": {
      title: "How our preventive check-ups work – Netherlands",
      description:
        "From booking to results — learn how our preventive health check-ups work, step by step.",
    },
  },
  contact: {
    en: {
      title: "Contact us",
      description:
        "Get in touch with the Ahead Health team by phone, email, WhatsApp or LinkedIn. We answer your questions about check-ups, bookings and results.",
    },
    de: {
      title: "Kontakt",
      description:
        "Kontaktieren Sie das Team von Ahead Health per Telefon, E-Mail, WhatsApp oder LinkedIn. Wir beantworten Ihre Fragen zu Check-ups, Buchungen und Ergebnissen.",
    },
    fr: {
      title: "Contactez-nous",
      description:
        "Contactez l'équipe Ahead Health par téléphone, e-mail, WhatsApp ou LinkedIn. Nous répondons à vos questions sur les bilans, les réservations et les résultats.",
    },
    nl: {
      title: "Contact",
      description:
        "Neem contact op met ons team via telefoon, e-mail, WhatsApp of LinkedIn. Vind uw dichtstbijzijnde locatie.",
    },
    "en-nl": {
      title: "Contact us – Netherlands",
      description:
        "Get in touch with our team via phone, email, WhatsApp, or LinkedIn. Find your nearest location.",
    },
  },
  about: {
    en: {
      title: "About us",
      description:
        "Meet the team behind Ahead Health: Swiss doctors, radiologists and engineers who combine medical expertise with AI-powered diagnostics for preventive check-ups.",
    },
    de: {
      title: "Über Ahead Health",
      description:
        "Lernen Sie das Team hinter Ahead Health kennen: Schweizer Ärztinnen, Ärzte und Ingenieure, die medizinische Expertise mit KI-gestützter Diagnostik verbinden.",
    },
    "de-at": {
      title: "Über Ahead Health – Österreich",
      description:
        "Lernen Sie das Team hinter Ahead Health kennen: Ärzte und Ingenieure, die medizinische Expertise mit KI-gestützter Diagnostik für Ihr Ganzkörper-MRT verbinden.",
    },
    fr: {
      title: "À propos",
      description:
        "Découvrez l'équipe d'Ahead Health : des médecins et ingénieurs suisses qui allient expertise médicale et diagnostic assisté par l'IA pour vos bilans préventifs.",
    },
    nl: {
      title: "Over ons",
      description:
        "Maak kennis met het team achter Ahead Health. Medische expertise ontmoet AI-gestuurde diagnostiek.",
    },
    "en-nl": {
      title: "About us",
      description:
        "Meet the team behind Ahead Health. Medical expertise meets AI-powered diagnostics.",
    },
    "en-de": {
      title: "About us",
      description:
        "Meet the team behind Ahead Health: Swiss doctors review your Prevention Starter Blood Test, with blood draws at German partner clinics and AI-powered analysis.",
    },
    "de-de": {
      title: "Über Ahead Health – Deutschland",
      description:
        "Lernen Sie das Team von Ahead Health kennen: Schweizer Ärzte werten Ihren Vorsorge-Starter-Bluttest aus, die Blutabnahme erfolgt in deutschen Partnerkliniken.",
    },
  },
  locations: {
    en: {
      title: "Clinic locations",
      description:
        "Find an Ahead Health partner clinic in Switzerland for your full-body MRI or blood test check-up, in Zurich, Geneva, Basel, Bern, Lausanne, Zug and more.",
    },
    de: {
      title: "Standorte",
      description:
        "Finden Sie eine Ahead Partnerklinik in der Schweiz für Ihr Ganzkörper-MRI oder Ihren Bluttest: in Zürich, Genf, Basel, Bern, Lausanne, Zug und weiteren Städten.",
    },
    // Austria has no clinic yet: five cities are coming soon, waitlist only.
    // Override keys skip the market suffix, so the country is in the title.
    "de-at": {
      title: "Standorte – Österreich",
      description:
        "Ganzkörper-MRT-Check-ups bald in Wien, Graz, Linz, Salzburg und Innsbruck. Setzen Sie sich auf die Warteliste und erfahren Sie zuerst, wann Termine frei sind.",
    },
    "de-de": {
      title: "Standorte",
      description:
        "Ihre Ahead Partnerklinik für den Vorsorge-Starter-Bluttest in Deutschland: Berlin, Frankfurt, Hamburg und München, mit ärztlich geprüften Ergebnissen.",
    },
    fr: {
      title: "Nos centres",
      description:
        "Trouvez un centre partenaire Ahead Health en Suisse pour votre IRM corps entier ou votre bilan sanguin : Genève, Lausanne, Zurich, Bâle, Berne, Zoug et plus.",
    },
    nl: {
      title: "Locaties",
      description:
        "Vind een kliniek voor preventieve gezondheidsonderzoeken bij u in de buurt.",
    },
    "en-nl": {
      title: "Clinic locations",
      description: "Find a preventive health check-up clinic near you.",
    },
    "en-de": {
      title: "Clinic locations",
      description:
        "Find an Ahead Health partner clinic in Germany for your Prevention Starter Blood Test, in Berlin, Frankfurt, Hamburg and Munich, with doctor-reviewed results.",
    },
  },
  faq: {
    en: {
      title: "Frequently asked questions",
      description:
        "Answers to common questions about Ahead Health check-ups: full-body MRI, advanced blood tests, preparation, results, pricing, insurance and membership.",
    },
    de: {
      title: "Häufig gestellte Fragen",
      description:
        "Antworten auf häufige Fragen zu den Check-ups von Ahead Health: Ganzkörper-MRI, erweiterte Bluttests, Vorbereitung, Ergebnisse, Preise und Versicherung.",
    },
    "de-at": {
      title: "Häufig gestellte Fragen – Österreich",
      description:
        "Häufig gestellte Fragen zu unseren Ganzkörper-MRT-Check-ups in Österreich: Ablauf, Vorbereitung, Ergebnisse, Preise und die Warteliste für die ersten Termine.",
    },
    fr: {
      title: "Questions fréquentes",
      description:
        "Réponses aux questions fréquentes sur les bilans Ahead Health : IRM corps entier, bilan sanguin avancé, préparation, résultats, prix et assurance.",
    },
    nl: {
      title: "Veelgestelde vragen",
      description:
        "Veelgestelde vragen over preventieve gezondheidsonderzoeken, MRI-scans en bloedtesten.",
    },
    "en-de": {
      title: "Frequently asked questions",
      description:
        "Answers to common questions about the Ahead Health Prevention Starter Blood Test in Germany: preparation, the blood draw, results, pricing and reimbursement.",
    },
    "de-de": {
      title: "Häufig gestellte Fragen – Deutschland",
      description:
        "Antworten auf häufige Fragen zum Vorsorge-Starter-Bluttest: Vorbereitung, Blutabnahme in der Partnerklinik, Ergebnisse, Preise und Kostenübernahme.",
    },
    "en-nl": {
      title: "Frequently asked questions – Netherlands",
      description:
        "Frequently asked questions about preventive health check-ups, MRI scans, and blood tests.",
    },
  },
  report: {
    en: {
      title: "Your personal health report",
      description:
        "Your personal Ahead Health report: results from your MRI and blood tests, explained in plain language, reviewed by Swiss doctors, with a personal action plan.",
    },
    de: {
      title: "Ihr persönlicher Gesundheitsbericht",
      description:
        "Ihr persönlicher Gesundheitsbericht: Ergebnisse aus MRI und Bluttests, verständlich erklärt und von Schweizer Ärzten geprüft, mit persönlichem Aktionsplan.",
    },
    "de-at": {
      title: "Ihr persönlicher Gesundheitsbericht – Österreich",
      description:
        "Ihr Gesundheitsbericht nach dem Ganzkörper-MRT: verständlich erklärte Befunde, ärztlich geprüft, mit konkreten Erkenntnissen und persönlichem Aktionsplan.",
    },
    // Germany sells blood tests only, so its report is a blood-test report.
    // Restore the shared `de` entry when DE_EXPANSION opens MRI booking.
    "de-de": {
      title: "Ihr persönlicher Gesundheitsbericht",
      description:
        "Zugang zu Ihrem persönlichen Gesundheitsbericht mit umsetzbaren Erkenntnissen aus Ihrem Bluttest, ärztlich geprüft und verständlich erklärt.",
    },
    fr: {
      title: "Votre rapport de santé personnalisé",
      description:
        "Votre rapport de santé Ahead : résultats de votre IRM et analyses sanguines expliqués simplement, validés par des médecins suisses, avec un plan d'action.",
    },
    nl: {
      title: "Uw persoonlijk gezondheidsrapport",
      description:
        "Bekijk uw persoonlijk gezondheidsrapport met concrete inzichten uit uw MRI en bloedtesten.",
    },
    "en-de": {
      title: "Your personal health report",
      description:
        "Access your personal health report with actionable insights from your blood test, reviewed by a doctor and clearly explained.",
    },
    "en-nl": {
      title: "Your personal health report – Netherlands",
      description:
        "Access your personal health report with actionable insights from your MRI and blood tests.",
    },
  },
  careers: {
    en: {
      title: "Careers",
      description:
        "Join Ahead Health and help build Europe's most advanced preventive health network. Browse our open roles and find out how we work and what we offer.",
    },
    de: {
      title: "Karriere",
      description:
        "Werde Teil von Ahead Health und hilf mit, Europas fortschrittlichstes Netzwerk für präventive Gesundheit aufzubauen. Entdecke unsere offenen Stellen.",
    },
    fr: {
      title: "Carrières",
      description:
        "Rejoignez Ahead Health et aidez-nous à bâtir le réseau de santé préventive le plus avancé d'Europe. Découvrez nos postes ouverts et notre façon de travailler.",
    },
    nl: {
      title: "Vacatures",
      description:
        "Sluit je aan bij ons team en help mee aan het meest geavanceerde preventieve gezondheidsnetwerk van Europa.",
    },
    "en-nl": {
      title: "Careers – Netherlands",
      description:
        "Join us and help build Europe's most advanced preventive health network.",
    },
  },
  insurance: {
    en: {
      title: "Insurance coverage",
      description:
        "Find out how much your Swiss supplementary insurance can cover for a preventive check-up. Check your plan in a few clicks and request the documents you need.",
    },
    de: {
      title: "Kostenübernahme",
      description:
        "Erfahren Sie, wie viel Ihre Zusatzversicherung an einen Check-up zahlt. Prüfen Sie Ihre Deckung in wenigen Klicks und fordern Sie die nötigen Unterlagen an.",
    },
    fr: {
      title: "Couverture d'assurance",
      description:
        "Découvrez quelle part de votre bilan préventif votre complémentaire peut couvrir. Vérifiez votre couverture en quelques clics et obtenez vos documents.",
    },
    nl: {
      title: "Verzekeringsdekking",
      description:
        "Ontdek hoeveel uw aanvullende verzekering vergoedt voor preventieve gezondheidsonderzoeken.",
    },
    "en-nl": {
      title: "Insurance coverage – Netherlands",
      description:
        "Find out how much your supplementary insurance covers for preventive health check-ups.",
    },
  },
  ambassadors: {
    en: {
      title: "Ahead Ambassador Program",
      description:
        "Join the Ahead Health ambassador program. For creators serious about performance, recovery, and long-term health — share your honest experience with our preventive check-ups.",
    },
    de: {
      title: "Ahead Ambassador-Programm",
      description:
        "Werden Sie Teil des Ahead Health Ambassador-Programms. Für Creators, die Leistung, Regeneration und langfristige Gesundheit ernst nehmen — teilen Sie Ihre echten Erfahrungen mit unseren Vorsorge-Check-ups.",
    },
  },
  ambassadorsApplied: {
    en: {
      title: "Application received",
      description:
        "Thanks for applying to the Ahead Health ambassador program. We'll be in touch within a few days.",
    },
    de: {
      title: "Bewerbung erhalten",
      description:
        "Danke für Ihre Bewerbung beim Ahead Health Ambassador-Programm. Wir melden uns innerhalb weniger Tage bei Ihnen.",
    },
  },
  kostenuebernahme: {
    "de-de": {
      title: "Kostenübernahme",
      description:
        "Ahead ist eine Selbstzahler-Leistung. Informationen zur Erstattung bei privaten Krankenversicherungen und akzeptierten Zahlungsmethoden.",
    },
    "en-de": {
      title: "Costs and reimbursement",
      description:
        "Ahead is a self-pay service. Information on reimbursement by private health insurance (PKV) and the payment methods we accept.",
    },
  },
  freeCall: {
    en: {
      title: "Free advisory call",
      description:
        "Book a free 15-minute call with the Ahead Health team. We answer your questions and help you choose the check-up that fits your health goals.",
    },
    de: {
      title: "Kostenloses Beratungsgespräch",
      description:
        "Buchen Sie ein kostenloses 15-minütiges Gespräch mit dem Team von Ahead Health. Wir beantworten Ihre Fragen und helfen Ihnen, den passenden Check-up zu finden.",
    },
    fr: {
      title: "Appel consultatif gratuit",
      description:
        "Réservez un appel gratuit de 15 minutes avec l'équipe Ahead Health. Nous répondons à vos questions et vous aidons à choisir le bilan adapté à vos objectifs.",
    },
    nl: {
      title: "Gratis adviesgesprek",
      description:
        "Boek een gratis adviesgesprek van 15 minuten met ons team over uw gezondheidsonderzoek.",
    },
    "en-nl": {
      title: "Free advisory call – Netherlands",
      description:
        "Book a free 15-minute advisory call with our team to discuss your health check-up options.",
    },
  },
  insights: {
    en: {
      title: "Health insights & resources",
      description:
        "Expert perspectives on prevention, early detection and longevity from our medical and scientific team, with practical guidance on biomarkers and health.",
    },
    de: {
      title: "Gesundheitswissen & Ressourcen",
      description:
        "Expertenperspektiven zu Vorsorge, Früherkennung und Langlebigkeit von unserem medizinischen und wissenschaftlichen Team.",
    },
    fr: {
      title: "Conseils santé & ressources",
      description:
        "Les perspectives de notre équipe médicale et scientifique sur la prévention, le dépistage précoce et la longévité, avec des conseils concrets pour votre santé.",
    },
    nl: {
      title: "Gezondheidskennis & informatie",
      description:
        "Expertinzichten over preventie en een lang, gezond leven van ons medisch en wetenschappelijk team.",
    },
    "en-nl": {
      title: "Health insights & resources",
      description:
        "Expert perspectives on prevention and longevity from our medical and scientific team.",
    },
  },
  biomarkers: {
    en: {
      title: "Biomarkers we test",
      description:
        "Explore every biomarker in our MRI and blood test check-ups: what each one measures, its reference range and what high or low values can mean for your health.",
    },
    de: {
      title: "Biomarker, die wir testen",
      description:
        "Alle Biomarker unserer MRI- und Bluttest-Check-ups im Überblick: was jeder Wert misst, welcher Bereich normal ist und was hohe oder tiefe Werte bedeuten können.",
    },
    "de-at": {
      title: "Biomarker, die wir testen – Österreich",
      description:
        "Alle Biomarker unserer Ganzkörper-MRT-Check-ups im Überblick: was jeder Wert misst, welcher Bereich als normal gilt und was auffällige Werte bedeuten können.",
    },
    // Germany sells the Vorsorge-Starter blood test only, so /de/de/biomarkers lists
    // the markers a blood report comes back with and nothing imaging. The
    // snippet Google shows must match, with no MRI in it. Restore the shared
    // `de` entry when DE_EXPANSION opens MRI booking in Germany.
    "de-de": {
      title: "Biomarker, die wir im Bluttest messen",
      description:
        "Alle Biomarker unseres Vorsorge-Starter-Bluttests und seiner Zusatzanalysen: Herz, Leber, Nieren, Stoffwechsel, Schilddrüse, Hormone und Blutbild.",
    },
    fr: {
      title: "Biomarqueurs testés",
      description:
        "Tous les biomarqueurs de nos bilans IRM et analyses sanguines : ce que mesure chaque valeur, sa plage de référence et ce que signifie une valeur haute ou basse.",
    },
    nl: {
      title: "Biomarkers die wij testen",
      description:
        "Volledige lijst van biomarkers die worden getest bij onze MRI- en bloedtestonderzoeken.",
    },
    "en-de": {
      title: "Biomarkers we measure in our blood test",
      description:
        "Every biomarker in our Prevention Starter Blood Test and its add-on analyses: heart, liver, kidneys, metabolism, thyroid, hormones and blood count.",
    },
    "en-nl": {
      title: "Biomarkers we test – Netherlands",
      description:
        "Full list of biomarkers tested in our MRI and blood test check-ups.",
    },
  },
  whatWeDetect: {
    en: {
      title: "Health conditions we check for",
      description:
        "Over 500 health conditions and risks checked in our MRI and blood test check-ups, from heart and blood vessels to liver, kidneys and metabolism.",
    },
    de: {
      title: "Gesundheitsrisiken, die wir prüfen",
      description:
        "Über 500 Gesundheitsrisiken, die wir in unseren MRI- und Bluttest-Check-ups prüfen: von Herz und Gefässen über Leber und Nieren bis zum Stoffwechsel.",
    },
    // Austria sells the full-body MRI packages (blood panels inside Advanced
    // and Pro, blood add-ons only on top), so its snippet names the MRT and
    // drops the "over 500" count the Swiss MRI + blood offer carried.
    "de-at": {
      title: "Gesundheitsrisiken, die wir prüfen – Österreich",
      description:
        "Gesundheitsrisiken, die wir mit unseren Ganzkörper-MRT-Check-ups erkennen: von Gehirn, Herz und Gefäßen über Leber und Nieren bis zum Stoffwechsel.",
    },
    // Germany sells the blood check-up only, so /de/de/what-we-detect lists the
    // risks the Prevention Starter panel finds and nothing imaging. The
    // snippet Google
    // shows must match: no MRI, and no "over 500" claim that only held
    // because the scan carried it. Restore the shared `de` entry when
    // DE_EXPANSION opens MRI booking in Germany.
    "de-de": {
      title: "Gesundheitsrisiken, die wir im Bluttest prüfen",
      description:
        "Welche Gesundheitsrisiken der Vorsorge-Starter-Bluttest mit %panelCount% Biomarkern aufdeckt: Herz, Leber, Nieren, Stoffwechsel, Schilddrüse und Blutbild.",
    },
    fr: {
      title: "Conditions détectées",
      description:
        "Plus de 500 pathologies et risques de santé vérifiés par nos bilans IRM et analyses sanguines : cœur et vaisseaux, foie, reins, métabolisme et plus encore.",
    },
    nl: {
      title: "Aandoeningen die wij opsporen",
      description:
        "Meer dan 500 gezondheidsproblemen die worden opgespoord via onze MRI- en bloedtestonderzoeken.",
    },
    "en-de": {
      title: "Health risks we check in our blood test",
      description:
        "The health risks the Prevention Starter Blood Test with %panelCount% biomarkers looks at: heart, liver, kidneys, metabolism, thyroid and blood count.",
    },
    "en-nl": {
      title: "Health conditions we check for – Netherlands",
      description:
        "Over 500 health conditions checked in our MRI and blood test check-ups.",
    },
  },
  tests: {
    en: {
      title: "Our tests",
      description:
        "Explore our preventive health tests: full-body MRI packages, advanced blood tests and targeted add-ons, reviewed by Swiss doctors with a personal action plan.",
    },
    de: {
      title: "Unsere Tests",
      description:
        "Entdecken Sie unsere präventiven Gesundheitstests: Ganzkörper-MRI-Pakete, erweiterte Bluttests und gezielte Zusatztests, ärztlich geprüft und mit Aktionsplan.",
    },
    // Germany sells the blood check-up and its add-ons, nothing else, so
    // /de/de/tests lists only the five blood rows. The shared `de` entry
    // promises "Ganzkörper-MRI", which the page no longer shows, and this is
    // the snippet Google serves. Same call as the `de-de` overrides on home,
    // /pricing and /how-it-works above. Restore the shared `de` entry when
    // DE_EXPANSION opens MRI booking.
    "de-de": {
      title: "Unsere Bluttests in Deutschland",
      description:
        "Der Vorsorge-Starter-Bluttest mit %panelCount% Biomarkern und die gezielten Zusatztests für Hormone, B-Vitamine und Omega-3. Ärztlich geprüfte Ergebnisse mit Aktionsplan.",
    },
    fr: {
      title: "Nos tests",
      description:
        "Découvrez nos tests de santé préventifs : IRM corps entier Core, Advanced et Pro, bilan sanguin avancé et analyses ciblées, avec un plan d'action personnalisé.",
    },
    nl: {
      title: "Onze testen",
      description:
        "Ontdek ons aanbod preventieve bloedtests, met Holistic MRI binnenkort beschikbaar.",
    },
    "en-nl": {
      title: "Our tests",
      description:
        "Explore our range of preventive blood tests, with Holistic MRI coming soon.",
    },
    "en-de": {
      title: "Our blood tests in Germany",
      description:
        "The Prevention Starter Blood Test with %panelCount% biomarkers and targeted add-on tests for hormones, B vitamins and omega-3. Doctor-reviewed results with an action plan.",
    },
    "de-at": {
      title: "Unsere Tests – Österreich",
      description:
        "Entdecken Sie unsere Ganzkörper-MRT-Check-ups in Österreich: Früherkennung für Gehirn, Organe und Gefäße, mit ärztlich geprüften Ergebnissen und Aktionsplan.",
    },
  },
  compareHealthCheckUps: {
    en: {
      title: "Compare preventive health check-ups in Switzerland",
      description:
        "Compare preventive health check-ups in Switzerland and find the right option. See how Ahead Health stacks up against typical providers and Swiss GPs.",
    },
    de: {
      title: "Vorsorge-Check-ups in der Schweiz vergleichen",
      description:
        "Vergleichen Sie Schweizer Vorsorge-Check-ups und finden Sie die für Sie passende Option. Sehen Sie, wie sich das Angebot von Ahead Health von anderen Anbietern und Schweizer Hausärzten unterscheidet.",
    },
    fr: {
      title: "Comparer les bilans de santé préventifs en Suisse",
      description:
        "Comparez les bilans de santé préventifs en Suisse et trouvez l'option qui vous convient. Découvrez comment Ahead Health se compare aux prestataires classiques et aux médecins généralistes suisses.",
    },
    nl: {
      title: "Vergelijk preventieve gezondheidsonderzoeken in Zwitserland",
      description:
        "Vergelijk preventieve gezondheidsonderzoeken in Zwitserland en vind de juiste optie. Zie hoe Ahead Health zich verhoudt tot reguliere aanbieders en Zwitserse huisartsen.",
    },
    "en-nl": {
      title: "Compare preventive health check-ups in Switzerland",
      description:
        "Compare preventive health check-ups in Switzerland and find the right option. See how Ahead Health stacks up against typical providers and Swiss GPs.",
    },
  },
  privacy: {
    en: {
      title: "Privacy policy",
      description:
        "How Ahead Health collects, uses and protects your personal and health data, who we share it with, how long we keep it and how to exercise your rights.",
    },
    de: {
      title: "Datenschutz",
      description:
        "Wie Ahead Health Ihre persönlichen und Gesundheitsdaten erhebt, nutzt und schützt, an wen wir sie weitergeben, wie lange wir sie speichern und Ihre Rechte.",
    },
    fr: {
      title: "Politique de confidentialité",
      description:
        "Comment Ahead Health collecte, utilise et protège vos données personnelles et de santé, avec qui nous les partageons, combien de temps et quels sont vos droits.",
    },
    nl: {
      title: "Privacybeleid",
      description: "Privacybeleid van Ahead Health.",
    },
    "en-de": {
      title: "Privacy policy for Germany",
      description:
        "Privacy policy for Ahead Health in Germany: how we process your personal and health data under the GDPR. English translation; the German version is binding.",
    },
    "en-nl": {
      title: "Privacy policy – Netherlands",
      description: "Privacy policy for Ahead Health.",
    },
  },
  membershipTerms: {
    en: {
      title: "Membership terms & conditions",
      description:
        "Terms and conditions for the Ahead Health membership: what is included, how billing and automatic renewal work, and how to change or cancel your membership.",
    },
    de: {
      title: "Mitgliedschaft AGB",
      description:
        "AGB der Ahead Mitgliedschaft: was enthalten ist, wie Abrechnung und automatische Verlängerung funktionieren und wie Sie Ihre Mitgliedschaft kündigen.",
    },
    fr: {
      title: "CGV abonnement",
      description:
        "Conditions générales de l'abonnement Ahead : ce qui est inclus, facturation et renouvellement automatique, et comment modifier ou résilier votre abonnement.",
    },
    "en-nl": {
      title: "Membership terms & conditions – Netherlands",
      description:
        "Terms and conditions for the Ahead Health membership programme.",
    },
    nl: {
      title: "Membership terms & conditions",
      description:
        "Terms and conditions for the Ahead Health membership programme.",
    },
  },
  terms: {
    en: {
      title: "Terms & conditions",
      description:
        "General terms and conditions for Ahead Health services: booking, payment, cancellation and rescheduling, how your examinations work and our responsibilities.",
    },
    de: {
      title: "AGB",
      description:
        "Allgemeine Geschäftsbedingungen von Ahead Health: Buchung, Zahlung, Stornierung und Umbuchung, Ablauf Ihrer Untersuchungen und unsere Verantwortlichkeiten.",
    },
    fr: {
      title: "Conditions générales",
      description:
        "Conditions générales d'Ahead Health : réservation, paiement, annulation et report, déroulement de vos examens et nos responsabilités envers vous.",
    },
    nl: {
      title: "Algemene voorwaarden",
      description: "Algemene voorwaarden van Ahead Health.",
    },
    "en-de": {
      title: "Terms and conditions for Germany",
      description:
        "General terms and conditions for Ahead Health services in Germany. English translation; the German version is binding.",
    },
    "en-nl": {
      title: "Terms & conditions – Netherlands",
      description: "Terms and conditions for Ahead Health services.",
    },
  },
  "insurance-checker-terms": {
    en: {
      title: "Reimbursement Assurance Programme Terms and Conditions",
      description:
        "Terms and conditions for the Reimbursement Assurance Programme, including coverage checker terms, eligibility, and claims process.",
    },
    de: {
      title: "Erstattungsabsicherung-Programm — Teilnahmebedingungen",
      description:
        "Bedingungen des Erstattungsabsicherungs-Programms, einschliesslich Deckungsprüfer-Bedingungen, Berechtigung und Schadensfall-Prozess.",
    },
    fr: {
      title:
        "Programme de protection du remboursement — Conditions de participation",
      description:
        "Conditions du Programme de protection du remboursement, y compris les conditions du vérificateur de couverture, l'éligibilité et la procédure de demande.",
    },
  },
  imprint: {
    en: {
      title: "Imprint",
      description:
        "Legal notice for Ahead Health: details of Ahead Health AG in Zurich, our authorised representatives, company identification number and contact details.",
    },
    de: {
      title: "Impressum",
      description:
        "Impressum von Ahead Health: Anbieterangaben der Ahead Health AG in Zürich, vertretungsberechtigte Personen, Kontaktdaten und weitere rechtliche Informationen.",
    },
    fr: {
      title: "Mentions légales",
      description:
        "Mentions légales d'Ahead Health : informations sur Ahead Health AG à Zurich, représentants autorisés, numéro d'identification de l'entreprise et coordonnées.",
    },
    nl: {
      title: "Colofon",
      description: "Wettelijke informatie en colofon van Ahead Health.",
    },
    "en-de": {
      title: "Imprint for Germany",
      description:
        "Imprint (Impressum) and legal information for Ahead Health in Germany, pursuant to § 5 DDG: provider details, authorised representatives and contact details.",
    },
    "en-nl": {
      title: "Imprint – Netherlands",
      description: "Legal information and imprint for Ahead Health.",
    },
  },
  authors: {
    en: {
      title: "Our authors",
      description:
        "Meet the doctors, radiologists and scientists who write and review Ahead Health insights, with their background, specialties and the articles they wrote.",
    },
    de: {
      title: "Unsere Autoren",
      description:
        "Lernen Sie die Ärztinnen, Ärzte und Fachleute kennen, die unsere Gesundheitsartikel schreiben und prüfen: ihr Werdegang, ihre Fachgebiete und Beiträge.",
    },
    fr: {
      title: "Nos auteurs",
      description:
        "Découvrez les médecins, radiologues et scientifiques qui rédigent et relisent les articles d'Ahead Health : parcours, spécialités et publications.",
    },
    nl: {
      title: "Onze auteurs",
      description:
        "Maak kennis met de medische experts en auteurs achter onze gezondheidsartikelen.",
    },
    "en-nl": {
      title: "Our authors – Netherlands",
      description:
        "Meet the medical experts and authors behind our health insights.",
    },
  },
  medicalDataSharing: {
    en: {
      title: "Medical data sharing consent",
      description:
        "Consent for sharing your medical data with Ahead Health and our partner labs, clinics and physicians: what is shared, why, and how to withdraw your consent.",
    },
    de: {
      title: "Einwilligung zur medizinischen Datenweitergabe",
      description:
        "Ihre Einwilligung, medizinische Daten mit Ahead Health und Partnerlaboren, Kliniken und Ärzten zu teilen: was geteilt wird, wozu und wie Sie widerrufen.",
    },
    fr: {
      title: "Consentement au partage de données médicales",
      description:
        "Consentement au partage de vos données médicales avec Ahead Health et nos laboratoires, centres et médecins partenaires : quoi, pourquoi et comment le retirer.",
    },
    nl: {
      title: "Toestemming medische gegevens delen",
      description:
        "Toestemming voor het delen van medische gegevens met Ahead Health.",
    },
    "en-nl": {
      title: "Medical data sharing consent – Netherlands",
      description: "Consent for sharing medical data with Ahead Health.",
    },
  },
  goBloodtest: {
    en: {
      title: "Comprehensive blood tests in Switzerland",
      description:
        "From cholesterol to vitamins to hormones — test up to 150 biomarkers and get real answers about your health in just a few days.",
    },
    de: {
      title: "Umfassende Bluttests in der Schweiz",
      description:
        "Von Cholesterin über Vitamine bis zu Hormonen: Testen Sie bis zu 150 Biomarker und erhalten Sie echte Antworten über Ihre Gesundheit in nur wenigen Tagen.",
    },
    // Without this, /de/de falls back to the `de` (Swiss) entry and ships
    // "…in der Schweiz" as the tab title and og:title of a German ad LP,
    // while the H1 on the page says "in Deutschland".
    "de-de": {
      title: "Umfassende Bluttests in Deutschland",
      description:
        "Von Cholesterin über Vitamine bis zu Hormonen: Testen Sie %panelCount% Biomarker und erhalten Sie fundierte Antworten zu Ihrer Gesundheit, in wenigen Tagen.",
    },
    fr: {
      title: "Bilans sanguins complets en Suisse",
      description:
        "Du cholestérol aux vitamines en passant par les hormones : testez jusqu'à 150 biomarqueurs et obtenez de vraies réponses sur votre santé en quelques jours.",
    },
    // NL: without
    // these, /nl/nl and /nl/en fall back to the Swiss `en` entry ("…in
    // Switzerland"). Count is the NL panel token, resolved from the CMS like the catalog.
    "nl-nl": {
      title: "Advanced bloedtest in Nederland",
      description:
        "Van cholesterol tot vitamines en hormonen: test %panelCount% biomarkers en krijg binnen een paar dagen heldere antwoorden over je gezondheid.",
    },
    "en-nl": {
      title: "Advanced blood test in the Netherlands",
      description:
        "From cholesterol to vitamins to hormones: test %panelCount% biomarkers and get clear answers about your health within a few days.",
    },
    "en-de": {
      title: "Comprehensive blood tests in Germany",
      description:
        "From cholesterol to vitamins to hormones: test %panelCount% biomarkers and get well-founded answers about your health within a few days.",
    },
  },
  goBloodtestMunich: {
    en: {
      title: "Blood test in Munich",
      description:
        "Blood test at Marienplatz in central Munich. A blood analysis with 80+ biomarkers, clear recommendations and results in days. No referral needed.",
    },
    de: {
      title: "Bluttest in München",
      description:
        "Bluttest am Marienplatz in der Münchner Innenstadt. Eine Blutanalyse mit %panelCount% Biomarkern, klare Empfehlungen und Ergebnisse in wenigen Tagen.",
    },
  },
  goBloodtestBerlin: {
    en: {
      title: "Blood test in Berlin",
      description:
        "Blood test in central Berlin. A blood analysis with 80+ biomarkers, clear recommendations and results in days. No referral needed.",
    },
    de: {
      title: "Bluttest in Berlin",
      description:
        "Bluttest in der Berliner Innenstadt. Eine Blutanalyse mit %panelCount% Biomarkern, klare Empfehlungen und Ergebnisse in wenigen Tagen.",
    },
  },
  goBloodtestFrankfurt: {
    en: {
      title: "Blood test in Frankfurt",
      description:
        "Blood test in central Frankfurt. A blood analysis with 80+ biomarkers, clear recommendations and results in days. No referral needed.",
    },
    de: {
      title: "Bluttest in Frankfurt",
      description:
        "Bluttest in der Frankfurter Innenstadt. Eine Blutanalyse mit %panelCount% Biomarkern, klare Empfehlungen und Ergebnisse in wenigen Tagen.",
    },
  },
  goBloodtestHamburg: {
    en: {
      title: "Blood test in Hamburg",
      description:
        "Blood test in central Hamburg. A blood analysis with 80+ biomarkers, clear recommendations and results in days. No referral needed.",
    },
    de: {
      title: "Bluttest in Hamburg",
      description:
        "Bluttest in der Hamburger Innenstadt. Eine Blutanalyse mit %panelCount% Biomarkern, klare Empfehlungen und Ergebnisse in wenigen Tagen.",
    },
  },
  goBloodtestZurich: {
    en: {
      title: "Blood test in Zurich",
      description:
        "Blood test in the heart of Zurich. Up to 150 biomarkers, clear recommendations from Swiss doctors, results in days. No referral needed.",
    },
    de: {
      title: "Bluttest in Zürich",
      description:
        "Bluttest im Herzen von Zürich. Bis zu 150 Biomarker, klare Empfehlungen von Schweizer Ärzten, Ergebnisse in wenigen Tagen. Keine Überweisung nötig.",
    },
  },
  goBloodtestLausanne: {
    en: {
      title: "Blood test in Lausanne",
      description:
        "Blood test in central Lausanne. Up to 150 biomarkers, clear recommendations from Swiss doctors, results in days. No referral needed.",
    },
    de: {
      title: "Bluttest in Lausanne",
      description:
        "Bluttest im Zentrum von Lausanne. Bis zu 150 Biomarker, klare Empfehlungen von Schweizer Ärzten, Ergebnisse in wenigen Tagen. Keine Überweisung nötig.",
    },
    fr: {
      title: "Bilan sanguin à Lausanne",
      description:
        "Bilan sanguin au centre de Lausanne. Jusqu'à 150 biomarqueurs, recommandations claires de médecins suisses, résultats en quelques jours. Sans ordonnance.",
    },
  },
  goBloodtestGeneva: {
    en: {
      title: "Blood test in Geneva",
      description:
        "Blood test in Geneva. Up to 150 biomarkers, clear recommendations from Swiss doctors, results in days. No referral needed.",
    },
    de: {
      title: "Bluttest in Genf",
      description:
        "Bluttest in Genf. Bis zu 150 Biomarker, klare Empfehlungen von Schweizer Ärzten, Ergebnisse in wenigen Tagen. Keine Überweisung nötig.",
    },
    fr: {
      title: "Bilan sanguin à Genève",
      description:
        "Bilan sanguin à Genève. Jusqu'à 150 biomarqueurs, recommandations claires de médecins suisses, résultats en quelques jours. Sans ordonnance.",
    },
  },
  goBloodtestSchaffhausen: {
    en: {
      title: "Blood test in Schaffhausen",
      description:
        "Blood test in Schaffhausen. Up to 150 biomarkers, clear recommendations from Swiss doctors, results in days. No referral needed.",
    },
    de: {
      title: "Bluttest in Schaffhausen",
      description:
        "Bluttest in Schaffhausen. Bis zu 150 Biomarker, klare Empfehlungen von Schweizer Ärzten, Ergebnisse in wenigen Tagen. Keine Überweisung nötig.",
    },
    fr: {
      title: "Bilan sanguin à Schaffhouse",
      description:
        "Bilan sanguin à Schaffhouse. Jusqu'à 150 biomarqueurs, recommandations claires de médecins suisses, résultats en quelques jours. Sans ordonnance.",
    },
  },
  goBloodtestZug: {
    en: {
      title: "Blood test in Zug",
      description:
        "Blood test in Zug. Up to 150 biomarkers, clear recommendations from Swiss doctors, results in days. No referral needed.",
    },
    de: {
      title: "Bluttest in Zug",
      description:
        "Bluttest in Zug. Bis zu 150 Biomarker, klare Empfehlungen von Schweizer Ärzten, Ergebnisse in wenigen Tagen. Keine Überweisung nötig.",
    },
    fr: {
      title: "Bilan sanguin à Zoug",
      description:
        "Bilan sanguin à Zoug. Jusqu'à 150 biomarqueurs, recommandations claires de médecins suisses, résultats en quelques jours. Sans ordonnance.",
    },
  },
  goBloodtestBasel: {
    en: {
      title: "Blood test in Basel",
      description:
        "Blood test in the heart of Basel. Up to 150 biomarkers, clear recommendations from Swiss doctors, results in days. No referral needed.",
    },
    de: {
      title: "Bluttest in Basel",
      description:
        "Bluttest im Herzen von Basel. Bis zu 150 Biomarker, klare Empfehlungen von Schweizer Ärzten, Ergebnisse in wenigen Tagen. Keine Überweisung nötig.",
    },
    fr: {
      title: "Bilan sanguin à Bâle",
      description:
        "Bilan sanguin au cœur de Bâle. Jusqu'à 150 biomarqueurs, recommandations claires de médecins suisses, résultats en quelques jours. Sans ordonnance.",
    },
  },
  goBloodtestWinterthur: {
    en: {
      title: "Blood test in Winterthur",
      description:
        "Blood test in Winterthur. Up to 150 biomarkers, clear recommendations from Swiss doctors, results in days. No referral needed.",
    },
    de: {
      title: "Bluttest in Winterthur",
      description:
        "Bluttest in Winterthur. Bis zu 150 Biomarker, klare Empfehlungen von Schweizer Ärzten, Ergebnisse in wenigen Tagen. Keine Überweisung nötig.",
    },
    fr: {
      title: "Bilan sanguin à Winterthour",
      description:
        "Bilan sanguin à Winterthour. Jusqu'à 150 biomarqueurs, recommandations claires de médecins suisses, résultats en quelques jours. Sans ordonnance.",
    },
  },
  goBloodtestBern: {
    en: {
      title: "Blood test in Bern",
      description:
        "Blood test in Bern. Up to 150 biomarkers, clear recommendations from Swiss doctors, results in days. No referral needed.",
    },
    de: {
      title: "Bluttest in Bern",
      description:
        "Bluttest in Bern. Bis zu 150 Biomarker, klare Empfehlungen von Schweizer Ärzten, Ergebnisse in wenigen Tagen. Keine Überweisung nötig.",
    },
    fr: {
      title: "Bilan sanguin à Berne",
      description:
        "Bilan sanguin à Berne. Jusqu'à 150 biomarqueurs, recommandations claires de médecins suisses, résultats en quelques jours. Sans ordonnance.",
    },
  },
  goBloodtestHorgen: {
    en: {
      title: "Blood test in Horgen",
      description:
        "Blood test in Horgen. Up to 150 biomarkers, clear recommendations from Swiss doctors, results in days. No referral needed.",
    },
    de: {
      title: "Bluttest in Horgen",
      description:
        "Bluttest in Horgen. Bis zu 150 Biomarker, klare Empfehlungen von Schweizer Ärzten, Ergebnisse in wenigen Tagen. Keine Überweisung nötig.",
    },
    fr: {
      title: "Bilan sanguin à Horgen",
      description:
        "Bilan sanguin à Horgen. Jusqu'à 150 biomarqueurs, recommandations claires de médecins suisses, résultats en quelques jours. Sans ordonnance.",
    },
  },
  goBloodtestStGallen: {
    en: {
      title: "Blood test in St. Gallen",
      description:
        "Blood test in St. Gallen. Up to 150 biomarkers, clear recommendations from Swiss doctors, results in days. No referral needed.",
    },
    de: {
      title: "Bluttest in St. Gallen",
      description:
        "Bluttest in St. Gallen. Bis zu 150 Biomarker, klare Empfehlungen von Schweizer Ärzten, Ergebnisse in wenigen Tagen. Keine Überweisung nötig.",
    },
    fr: {
      title: "Bilan sanguin à Saint-Gall",
      description:
        "Bilan sanguin à Saint-Gall. Jusqu'à 150 biomarqueurs, recommandations claires de médecins suisses, résultats en quelques jours. Sans ordonnance.",
    },
  },
  goFullbodymri: {
    en: {
      title: "Full-body MRI in Switzerland",
      description:
        "A preventive, radiation-free MRI reviewed by Swiss physicians — designed to detect early signs of health risks before symptoms appear.",
    },
    de: {
      title: "Ganzkörper-MRI in der Schweiz",
      description:
        "Ein präventives, strahlungsfreies MRI — geprüft von Schweizer Ärzten zur frühzeitigen Erkennung gesundheitlicher Risiken.",
    },
    "de-de": {
      title: "Ganzkörper-MRT in Deutschland – bald verfügbar",
      description:
        "Ein präventives, strahlungsfreies Ganzkörper-MRT zur frühzeitigen Erkennung gesundheitlicher Risiken – in Deutschland bald verfügbar.",
    },
    fr: {
      title: "IRM corps entier en Suisse",
      description:
        "Une IRM préventive, sans rayonnement, examinée par des médecins suisses, conçue pour détecter les premiers signes de risques pour la santé.",
    },
    "en-de": {
      title: "Full-body MRI in Germany: coming soon",
      description:
        "A preventive, radiation-free full-body MRI to spot health risks early, coming soon to Germany.",
    },
  },
  goFullbodymriZurich: {
    en: {
      title: "Full-body MRI in Zurich",
      description:
        "Preventive, radiation-free full-body MRI in Zurich and Thalwil. Reviewed by Swiss physicians, with a clear personal health report. No referral needed.",
    },
    de: {
      title: "Ganzkörper-MRI in Zürich",
      description:
        "Präventives, strahlungsfreies Ganzkörper-MRI in Zürich und Thalwil. Geprüft von Schweizer Ärzten, mit klarem persönlichem Gesundheitsbericht. Keine Überweisung nötig.",
    },
    fr: {
      title: "IRM corps entier à Zurich",
      description:
        "IRM corps entier préventive et sans rayonnement à Zurich et Thalwil. Validée par des médecins suisses, avec un rapport de santé clair. Sans ordonnance.",
    },
  },
  goFullbodymriBasel: {
    en: {
      title: "Full-body MRI in Basel",
      description:
        "Preventive, radiation-free full-body MRI in Basel and Rheinfelden. Reviewed by Swiss physicians, with a clear personal health report. No referral needed.",
    },
    de: {
      title: "Ganzkörper-MRI in Basel",
      description:
        "Präventives, strahlungsfreies Ganzkörper-MRI in Basel und Rheinfelden. Geprüft von Schweizer Ärzten, mit klarem persönlichem Gesundheitsbericht. Keine Überweisung nötig.",
    },
    fr: {
      title: "IRM corps entier à Bâle",
      description:
        "IRM corps entier préventive et sans rayonnement à Bâle et Rheinfelden. Validée par des médecins suisses, avec un rapport de santé clair. Sans ordonnance.",
    },
  },
  goFullbodymriGeneva: {
    en: {
      title: "Full-body MRI in Geneva",
      description:
        "Preventive, radiation-free full-body MRI in the heart of Geneva. Reviewed by Swiss physicians, with a clear personal health report. No referral needed.",
    },
    de: {
      title: "Ganzkörper-MRI in Genf",
      description:
        "Präventives, strahlungsfreies Ganzkörper-MRI im Herzen von Genf. Geprüft von Schweizer Ärzten, mit klarem persönlichem Gesundheitsbericht. Keine Überweisung nötig.",
    },
    fr: {
      title: "IRM corps entier à Genève",
      description:
        "IRM corps entier préventive et sans rayonnement au cœur de Genève. Validée par des médecins suisses, avec un rapport de santé clair. Sans ordonnance.",
    },
  },
  goFullbodymriZug: {
    en: {
      title: "Full-body MRI in Zug",
      description:
        "Preventive, radiation-free full-body MRI in Zug. Reviewed by Swiss physicians, with a clear personal health report. No referral needed.",
    },
    de: {
      title: "Ganzkörper-MRI in Zug",
      description:
        "Präventives, strahlungsfreies Ganzkörper-MRI in Zug. Geprüft von Schweizer Ärzten, mit klarem persönlichem Gesundheitsbericht. Keine Überweisung nötig.",
    },
    fr: {
      title: "IRM corps entier à Zoug",
      description:
        "IRM corps entier préventive et sans rayonnement à Zoug. Validée par des médecins suisses, avec un rapport de santé clair. Sans ordonnance.",
    },
  },
  goFullbodymriStGallen: {
    en: {
      title: "Full-body MRI in St. Gallen",
      description:
        "Preventive, radiation-free full-body MRI in St. Gallen. Reviewed by Swiss physicians, with a clear personal health report. No referral needed.",
    },
    de: {
      title: "Ganzkörper-MRI in St. Gallen",
      description:
        "Präventives, strahlungsfreies Ganzkörper-MRI in St. Gallen. Geprüft von Schweizer Ärzten, mit klarem persönlichem Gesundheitsbericht. Keine Überweisung nötig.",
    },
    fr: {
      title: "IRM corps entier à Saint-Gall",
      description:
        "IRM corps entier préventive et sans rayonnement à Saint-Gall. Validée par des médecins suisses, avec un rapport de santé clair. Sans ordonnance.",
    },
  },
  goCheckup: {
    en: {
      title: "Full-body check-up in Switzerland",
      description:
        "Detect early signs of health risks, including certain types of cancer and 550 other possible conditions, in just one check-up.",
    },
    de: {
      title: "Ganzkörper-Check-up in der Schweiz",
      description:
        "Prüfen Sie Anzeichen von Gesundheitsrisiken, einschliesslich mehrerer Krebsarten und über 550 weitere Auffälligkeiten in nur einem Termin.",
    },
    fr: {
      title: "Bilan corps entier en Suisse",
      description:
        "Détectez les premiers signes de risques pour la santé, dont certains types de cancer et 550 autres pathologies possibles, en un seul bilan.",
    },
    "en-de": {
      title: "Preventive blood check-up in Germany",
      description:
        "The Prevention Starter Blood Test with %panelCount% biomarkers at a German partner clinic, reviewed by Swiss doctors, with a personal action plan.",
    },
  },
  goInsurancemri: {
    en: {
      title: "Does your insurance cover an MRI check-up?",
      description:
        "Many Swiss supplementary insurers reimburse up to CHF 1,500 for preventive MRI scans. Check in 30 seconds if yours is included.",
    },
    de: {
      title: "MRI-Kosten: Wie viel übernimmt Ihre Versicherung?",
      description:
        "Viele Schweizer Zusatzversicherungen erstatten bis zu CHF 1'500 für präventive MRI-Scans. Prüfen Sie in 30 Sekunden, ob Ihre dazugehört.",
    },
    fr: {
      title: "Votre assurance couvre-t-elle un bilan IRM ?",
      description:
        "De nombreuses assurances complémentaires suisses remboursent jusqu'à CHF 1'500 pour les IRM préventives. Vérifiez en 30 secondes si la vôtre est incluse.",
    },
  },
  goInsuranceblood: {
    en: {
      title: "Does your insurance cover your blood test?",
      description:
        "Many Swiss supplementary insurers cover part or all of the cost of preventive blood tests. Check in 30 seconds if yours is included.",
    },
    de: {
      title: "Bluttest-Kosten: Wie viel übernimmt Ihre Versicherung?",
      description:
        "Viele Schweizer Zusatzversicherungen übernehmen einen Teil oder die gesamten Kosten für präventive Bluttests. Prüfen Sie in 30 Sekunden, ob Ihre dazugehört.",
    },
    fr: {
      title: "Votre assurance couvre-t-elle votre bilan sanguin ?",
      description:
        "De nombreuses assurances complémentaires suisses couvrent une partie ou la totalité du coût des bilans sanguins préventifs. Vérifiez en 30 secondes.",
    },
  },
  goMembership: {
    en: {
      title: "Ahead membership — year-round health monitoring",
      description:
        "Regular check-ups, personalised action plans, and expert guidance from Swiss doctors. Stay on top of your health with an Ahead membership.",
    },
    de: {
      title: "Ahead-Mitgliedschaft — ganzjährige Gesundheitsüberwachung",
      description:
        "Regelmässige Check-ups, persönliche Aktionspläne und fachkundige Begleitung durch Schweizer Ärzte. Behalten Sie Ihre Gesundheit im Blick.",
    },
    "de-de": {
      title: "Ahead-Mitgliedschaft – bald verfügbar in Deutschland",
      description:
        "Regelmäßige Check-ups, persönliche Aktionspläne und fachkundige Begleitung. Die Ahead-Mitgliedschaft ist in Deutschland bald verfügbar.",
    },
    fr: {
      title: "Abonnement Ahead — suivi santé tout au long de l'année",
      description:
        "Bilans réguliers, plans d'action personnalisés et accompagnement par des médecins suisses. Gardez le contrôle de votre santé.",
    },
    "en-de": {
      title: "Ahead membership: coming soon in Germany",
      description:
        "Regular check-ups, personal action plans and expert guidance. The Ahead membership is coming soon to Germany.",
    },
  },
  membership: {
    en: {
      title: "Ahead membership",
      description:
        "An ongoing membership for preventive health — full-body MRI, advanced blood panels, and a personal health plan reviewed by Swiss physicians, year after year.",
    },
    de: {
      title: "Ahead-Mitgliedschaft",
      description:
        "Eine fortlaufende Mitgliedschaft für präventive Gesundheit — Ganzkörper-MRI, erweiterte Blutpanels und ein persönlicher Gesundheitsplan, jährlich begleitet von Schweizer Ärzten.",
    },
    // Germany sells the Vorsorge-Starter blood test only, so the snippet names
    // no scan. Restore the shared `de` entry when DE_EXPANSION opens MRI booking.
    "de-de": {
      title: "Ahead-Mitgliedschaft",
      description:
        "Eine fortlaufende Mitgliedschaft für präventive Gesundheit — jährlicher Vorsorge-Starter-Bluttest mit %panelCount% Biomarkern, Folgetests und ein persönlicher Gesundheitsplan, begleitet von Schweizer Ärzten.",
    },
    fr: {
      title: "Abonnement Ahead",
      description:
        "Un abonnement continu pour la santé préventive — IRM corps entier, bilans sanguins avancés et un plan de santé personnel suivi par des médecins suisses, année après année.",
    },
    nl: {
      title: "Ahead-lidmaatschap",
      description:
        "Een doorlopend lidmaatschap voor preventieve gezondheid — Holistic MRI, uitgebreide bloedpanelen en een persoonlijk gezondheidsplan, jaar na jaar begeleid door artsen.",
    },
    "en-de": {
      title: "Ahead membership",
      description:
        "An ongoing membership for preventive health: a yearly Prevention Starter Blood Test with %panelCount% biomarkers, follow-up tests and a personal health plan, guided by Swiss doctors.",
    },
  },
  search: {
    en: {
      title: "Search",
      description:
        "Search across Ahead Health — biomarkers, diseases, articles, FAQs, and tests.",
    },
    de: {
      title: "Suche",
      description:
        "Durchsuche Ahead Health — Biomarker, Erkrankungen, Insights, FAQs und Tests.",
    },
    fr: {
      title: "Recherche",
      description:
        "Cherchez sur Ahead Health — biomarqueurs, maladies, articles, FAQ et tests.",
    },
    nl: {
      title: "Zoeken",
      description:
        "Doorzoek Ahead Health — biomarkers, aandoeningen, artikelen, FAQ's en tests.",
    },
  },
}

/** Page-specific OG images. Pages not listed here use /og/default.png. */
const OG_IMAGES: Record<string, string | Record<string, string>> = {
  report: "/og/report.png",
  membership: {
    en: "/og/membership-en.png",
    de: "/og/membership-de.png",
  },
}

/** The membership-first site's OG image per language (Eugene, 13.10): its
 *  line over the booking's tube caps. English stands in for a language
 *  without its own. */
const MEMBERSHIP_FIRST_OG_IMAGES: Record<string, string> = {
  en: "/og/membership-first.jpg",
  de: "/og/membership-first-de.jpg",
}

/** The OG image of a page without its own: the membership-first site has
 *  its own, the other markets `/og/default.png`. */
export function defaultOgImage(country: string, locale: string): string {
  if (!isMembershipFirstSite(country)) return "/og/default.png"
  return MEMBERSHIP_FIRST_OG_IMAGES[locale] ?? MEMBERSHIP_FIRST_OG_IMAGES.en
}

function resolveOgImage(page: string, locale: string, country: string): string {
  const entry = OG_IMAGES[page]
  if (!entry) return defaultOgImage(country, locale)
  if (typeof entry === "string") return entry
  return entry[locale] ?? entry.en ?? defaultOgImage(country, locale)
}

const BASE_URL = SITE_URL

/**
 * Page key → URL path mapping. Used to generate canonical URLs and hreflang alternates.
 */
const PAGE_PATHS: Record<string, string> = {
  home: "",
  quizFocus: "quiz-focus",
  pricing: "pricing",
  howItWorks: "how-it-works",
  reviews: "reviews",
  contact: "contact",
  about: "about",
  locations: "locations",
  faq: "faq",
  report: "report",
  careers: "careers",
  insurance: "insurance",
  kostenuebernahme: "kostenuebernahme",
  freeCall: "free-call",
  ambassadors: "ambassadors",
  ambassadorsApplied: "ambassadors/applied",
  insights: "insights",
  biomarkers: "biomarkers",
  whatWeDetect: "what-we-detect",
  tests: "tests",
  compareHealthCheckUps: "compare-health-check-ups-switzerland",
  privacy: "privacy",
  membershipTerms: "membership-terms",
  terms: "terms-conditions",
  "insurance-checker-terms": "insurance-checker-terms",
  imprint: "imprint",
  authors: "authors",
  medicalDataSharing: "medical-data-sharing",
  goBloodtest: "go/bloodtest",
  goBloodtestMunich: "go/bloodtest-munich",
  goBloodtestBerlin: "go/bloodtest-berlin",
  goBloodtestFrankfurt: "go/bloodtest-frankfurt",
  goBloodtestHamburg: "go/bloodtest-hamburg",
  goBloodtestZurich: "go/bloodtest-zurich",
  goBloodtestLausanne: "go/bloodtest-lausanne",
  goBloodtestGeneva: "go/bloodtest-geneva",
  goBloodtestSchaffhausen: "go/bloodtest-schaffhausen",
  goBloodtestZug: "go/bloodtest-zug",
  goBloodtestBasel: "go/bloodtest-basel",
  goBloodtestWinterthur: "go/bloodtest-winterthur",
  goBloodtestBern: "go/bloodtest-bern",
  goBloodtestHorgen: "go/bloodtest-horgen",
  goBloodtestStGallen: "go/bloodtest-st-gallen",
  goFullbodymri: "go/fullbodymri",
  goFullbodymriZurich: "go/fullbodymri-zurich",
  goFullbodymriBasel: "go/fullbodymri-basel",
  goFullbodymriGeneva: "go/fullbodymri-geneva",
  goFullbodymriZug: "go/fullbodymri-zug",
  goFullbodymriStGallen: "go/fullbodymri-st-gallen",
  goCheckup: "go/checkup",
  goInsurancemri: "go/insurancemri",
  goInsuranceblood: "go/insuranceblood",
  goMembership: "go/membership",
  membership: "membership",
  search: "search",
}

/** RFC 5646 underscore OG locale format (e.g. de_CH, fr_CH, nl_NL) */
export function getOgLocale(locale: string, country: string): string {
  return `${locale}_${country.toUpperCase()}`
}

/**
 * Page keys excluded from the search index — ad landing pages (paid traffic
 * only, see `/landing-pages` skill) and the gated discovery-call page.
 */
const SEARCH_EXCLUDED_PAGES = new Set([
  "quizFocus", // funnel entry point, not a content page — keep out of site search
  "freeCall",
  "goBloodtest",
  "goBloodtestMunich",
  "goBloodtestBerlin",
  "goBloodtestFrankfurt",
  "goBloodtestHamburg",
  "goBloodtestZurich",
  "goBloodtestLausanne",
  "goBloodtestGeneva",
  "goBloodtestSchaffhausen",
  "goBloodtestZug",
  "goBloodtestBasel",
  "goBloodtestWinterthur",
  "goBloodtestBern",
  "goBloodtestHorgen",
  "goBloodtestStGallen",
  "goFullbodymri",
  "goFullbodymriZurich",
  "goFullbodymriBasel",
  "goFullbodymriGeneva",
  "goFullbodymriZug",
  "goFullbodymriStGallen",
  "goCheckup",
  "goInsurancemri",
  "goInsuranceblood",
  "goMembership",
  "search", // searching for "search" → /search is silly, skip
])

export interface StaticPageEntry {
  pageKey: string
  /** URL path WITHOUT a leading slash. Empty string for the homepage. */
  path: string
  /**
   * Localized title + description. Same lookup logic as `getPageMetadata` —
   * the caller picks the right (country, locale) pair.
   */
  meta: Record<string, { title: string; description: string }>
}

/**
 * The list of static pages a site-search index should include — every entry
 * in `META` minus paid-LP and gated routes. Exported for the build-time
 * search-index generator (`scripts/build-search-index.ts`).
 */
export function listStaticPagesForSearchIndex(): StaticPageEntry[] {
  return Object.keys(PAGE_PATHS)
    .filter((key) => !SEARCH_EXCLUDED_PAGES.has(key))
    .filter((key) => META[key]) // only pages with localized metadata
    .map((key) => ({ pageKey: key, path: PAGE_PATHS[key]!, meta: META[key]! }))
}

/**
 * EVERY page in `META`, unfiltered — `SEARCH_EXCLUDED_PAGES` is a
 * search-index concern only. The market-matrix tests iterate this so the
 * excluded pages (paid `go*` landing pages, quizFocus, freeCall, search)
 * are still checked for locale-override reachability: #2260's wrong-cased
 * keys shipped on exactly this page class. `path` is null for META keys
 * without a PAGE_PATHS route.
 */
export function listAllPageMeta(): {
  pageKey: string
  path: string | null
  meta: Record<string, { title: string; description: string }>
}[] {
  return Object.keys(META).map((key) => ({
    pageKey: key,
    path: PAGE_PATHS[key] ?? null,
    meta: META[key]!,
  }))
}

/**
 * Localized title + description for a page key, applying the same fallback
 * order as `getPageMetadata` (country-locale → locale → en). Returns null
 * if the page key isn't in `META`.
 */
export function getPageStaticMeta(
  pageKey: string,
  locale: string,
  country: string
): { title: string; description: string } | null {
  const pageMeta = META[pageKey]
  if (!pageMeta) return null
  const countryLocaleKey = `${locale}-${country}`
  return pageMeta[countryLocaleKey] || pageMeta[locale] || pageMeta.en || null
}

/** hreflang alternates: hreflang tag → absolute URL. */
export type HreflangLanguages = Record<string, string>

/**
 * The hreflang loop shared by static-page metadata (`getPageMetadata`) and
 * dynamic/CMS-slug metadata (`getSlugAlternates`): one alternate per public
 * market, keyed by hreflang tag, emitted only where `emitted` allows.
 *
 * `urlFor` / `emitted` are exactly where the two callers diverge — the static
 * path keys its URL by locale (a hardcoded slug map, lib/localized-route-slugs.ts),
 * the CMS path by (locale, country) plus a per-document availability gate — so
 * they're injected. The `x-default` alternate is deliberately NOT set here:
 * the two paths choose it differently, so each caller sets `languages["x-default"]`
 * on the returned map itself.
 */
export function buildHreflangLanguages(
  urlFor: (lang: string, countryCode: Country) => string,
  emitted: (lang: string, countryCode: Country) => boolean
): HreflangLanguages {
  const languages: HreflangLanguages = {}
  for (const countryConfig of publicCountries) {
    for (const lang of countryConfig.languages) {
      if (!emitted(lang, countryConfig.code)) continue
      languages[getBcp47Locale(lang, countryConfig.code)] = urlFor(
        lang,
        countryConfig.code
      )
    }
  }
  return languages
}

/**
 * The shared `en`/`de`/`fr` META entries are written for Switzerland. Another
 * market reusing one (no `${locale}-${country}` override) gets its country
 * appended, so /nl/en and /ch/en — or /de/de and /ch/de — don't ship
 * byte-identical titles that compete in the same SERP.
 */
export function withMarketSuffix(
  title: string,
  locale: string,
  country: string
): string {
  if (country === "ch" || !CH_LANGUAGES.includes(locale)) return title
  const name = new Intl.DisplayNames([locale], { type: "region" }).of(
    country.toUpperCase()
  )
  return `${title} – ${name}`
}

const CH_LANGUAGES: readonly string[] = ["en", "de", "fr"]

/**
 * Get localized metadata for a page.
 * Falls back to English if locale not found.
 * Generates canonical URL and hreflang alternates (including x-default) from the page key.
 *
 * @param page - Page key from PAGE_PATHS
 * @param locale - Active locale (e.g. "en", "de", "nl")
 * @param country - Active country code (e.g. "ch", "nl"). Defaults to "ch".
 */
export function getPageMetadata(
  page: string,
  locale: string,
  country = "ch",
  options: { gate?: GateId } = {}
): Metadata {
  const pageMeta = META[page]
  if (!pageMeta) return {}
  // Country-specific metadata override (e.g. en-NL for English in Netherlands)
  const countryLocaleKey = `${locale}-${country}`
  const localized =
    pageMeta[countryLocaleKey] || pageMeta[locale] || pageMeta.en
  const title = pageMeta[countryLocaleKey]
    ? localized.title
    : withMarketSuffix(localized.title, locale, country)

  const pagePath = PAGE_PATHS[page] ?? ""
  // Pages with a translated URL (`lib/localized-route-slugs.ts`) have a different
  // path per language, so the suffix is resolved per hreflang entry rather
  // than shared. Everything else returns the same path for every locale.
  const pathFor = (lang: string) =>
    pagePath ? localizedPath(`/${pagePath}`, lang) : ""
  const pathSuffix = pathFor(locale)
  const canonical = `${BASE_URL}/${country}/${locale}${pathSuffix}`

  // hreflang entries from all enabled countries, filtered by gate if set.
  const languages = buildHreflangLanguages(
    (lang, code) => `${BASE_URL}/${code}/${lang}${pathFor(lang)}`,
    (_lang, code) => !options.gate || isAllowedInCountry(options.gate, code)
  )
  // x-default: if the gate excludes CH, fall back to the first allowed
  // country's default locale. Otherwise keep /ch/de (matches the
  // auto-redirect target for `/` and geo-picker default; German is also
  // the majority language of our actual visitors).
  // When the gate excludes CH, x-default falls back to the first country in
  // the gate's `countries` array. The ordering is declaration order in
  // COUNTRY_GATES — when adding a multi-country gate, list the canonical
  // fallback country first.
  if (options.gate && !isAllowedInCountry(options.gate, "ch")) {
    const firstAllowed = COUNTRY_GATES[options.gate].countries[0]
    const cfg = publicCountries.find((c) => c.code === firstAllowed)
    if (cfg) {
      languages["x-default"] =
        `${BASE_URL}/${cfg.code}/${cfg.defaultLanguage}${pathFor(cfg.defaultLanguage)}`
    } else {
      languages["x-default"] = `${BASE_URL}/ch/de${pathFor("de")}`
    }
  } else if (page === "home") {
    // `/` geo-redirects per visitor (proxy.ts `handleRoot`), so it IS the
    // language-neutral default — Google's documented x-default for an
    // auto-redirecting homepage. Pointing at /ch/de contradicted the redirect
    // a German visitor actually gets (/de/de).
    languages["x-default"] = `${BASE_URL}/`
  } else {
    languages["x-default"] = `${BASE_URL}/ch/de${pathFor("de")}`
  }

  const titleWithBrand = `${title} | Ahead Health`

  return {
    // Use `absolute` so the brand suffix is always rendered in the document
    // <title>, even on routes where Next.js doesn't propagate a parent
    // layout's `title.template` (e.g. /biomarkers and /what-we-detect, which
    // are URL-rewritten to /v/[code]/page.tsx via proxy.ts and lose the
    // template inheritance).
    title: { absolute: titleWithBrand },
    description: localized.description,
    openGraph: {
      title: titleWithBrand,
      description: localized.description,
      images: [
        {
          url: resolveOgImage(page, locale, country),
          width: 1200,
          height: 630,
        },
      ],
      url: canonical,
      type: "website",
      locale: getOgLocale(locale, country),
      siteName: "Ahead Health",
    },
    alternates: {
      canonical,
      languages,
    },
  }
}

/**
 * Plain localized page title (no brand suffix) from the same META table
 * `getPageMetadata` reads. Used as `pageTitle` context for the scoped chat
 * on listing pages, where there's no per-record name.
 */
export function getPageTitle(
  page: string,
  locale: string,
  country = "ch"
): string {
  const pageMeta = META[page]
  if (!pageMeta) return ""
  const localized =
    pageMeta[`${locale}-${country}`] || pageMeta[locale] || pageMeta.en
  return localized.title
}

/**
 * Build page metadata inside a `"use cache"` scope so Next 16 can include it
 * in the prerendered shell before hydration resumes.
 */
export async function getCachedPageMetadata(
  page: string,
  locale: string,
  country = "ch",
  options: { gate?: GateId } = {}
): Promise<Metadata> {
  return getPageMetadata(page, locale, country, options)
}

/**
 * Generate canonical URL and hreflang alternates for a dynamic (slug-based) page.
 * Use this in `generateMetadata` for detail pages like /insights/[slug], /tests/[slug], etc.
 *
 * @param path - Path after /{country}/{locale}/, e.g. "insights/my-article"
 * @param locale - Active locale
 * @param country - Active country code. Defaults to "ch".
 * @param options.localizedPath - For routes whose slug varies per locale
 *   (localized CMS slugs, AHD-10323): returns the path for a given
 *   (lang, country) pair; used for every alternate INCLUDING x-default,
 *   while `path` stays the canonical for the active locale.
 * @param options.availableIn - Per-market existence gate for THIS document.
 *   `options.gate` answers "does this route exist in that country"; this
 *   answers "does this particular document render there". A CMS doc can be
 *   hidden per market (`hideInCountries`) or simply untranslated in a
 *   fallback-suppressed locale (`de-DE` / `en-NL` / `de-AT`), in which case
 *   its route 404s and an hreflang pointing at it is invalid — Google can
 *   discard the whole cluster. Return false to drop that market's alternate.
 */
/**
 * Adapt a `${country}/${locale}` market set (from `marketsWhere` and friends)
 * into the `availableIn` predicate `getSlugAlternates` expects.
 *
 * Exists so the key format lives in exactly one place — four call sites were
 * about to inline the same template literal.
 */
export const availableInMarkets =
  (markets: ReadonlySet<string>) =>
  (lang: string, countryCode: string): boolean =>
    markets.has(`${countryCode}/${lang}`)

export function getSlugAlternates(
  path: string,
  locale: string,
  country = "ch",
  options: {
    gate?: GateId
    localizedPath?: (lang: string, countryCode: string) => string
    availableIn?: (lang: string, countryCode: string) => boolean
  } = {}
) {
  const pathFor = (lang: string, countryCode: string) =>
    options.localizedPath ? options.localizedPath(lang, countryCode) : path
  // The canonical is the URL THIS market serves, so it goes through the same
  // `localizedPath` as every alternate. Built from the raw `path` it named the
  // English route instead: all 28 insurer pages canonicalised to
  // `/ch/{de,fr}/insurance/<insurer>`, which only 308s back to
  // `/versicherung/<insurer>` — a canonical must name a URL that returns 200.
  const canonical = `${BASE_URL}/${country}/${locale}/${pathFor(locale, country)}`
  const urlFor = (lang: string, countryCode: string) =>
    `${BASE_URL}/${countryCode}/${lang}/${pathFor(lang, countryCode)}`
  const emitted = (lang: string, countryCode: Country) => {
    if (options.gate && !isAllowedInCountry(options.gate, countryCode)) {
      return false
    }
    return options.availableIn ? options.availableIn(lang, countryCode) : true
  }

  const languages = buildHreflangLanguages(urlFor, emitted)

  // x-default must be a URL we actually emitted — pointing it at a market the
  // document doesn't render in is the same invalid-cluster problem.
  //
  // Preference order: ch/de (the canonical default), then the gate's first
  // declared country (ordering is declaration order in COUNTRY_GATES — when
  // adding a multi-country gate, list the canonical fallback country first),
  // then whatever we did emit, and finally the canonical itself so the key is
  // never dangling.
  const gateFirst = options.gate
    ? publicCountries.find(
        (c) => c.code === COUNTRY_GATES[options.gate!].countries[0]
      )
    : undefined
  languages["x-default"] = emitted("de", "ch")
    ? urlFor("de", "ch")
    : gateFirst && emitted(gateFirst.defaultLanguage, gateFirst.code)
      ? urlFor(gateFirst.defaultLanguage, gateFirst.code)
      : (Object.values(languages)[0] ?? canonical)

  return { canonical, languages }
}
